import React, { useEffect, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import {
  ChipBadge,
  deserify,
  PrimaryButton,
  pxToRem,
  pxTovW,
  QuantityButton,
} from '@geneo2-web/shared-ui';
import {
  Question,
  Question_QuestionEnum,
} from '@protos/content_management/content.db_pb';
import { questionCountTypeWise } from '../utils';
import { useAppSelector } from 'apps/teacher/src/reduxStore/reduxHooks';
import { TaskCreationStatusEnum } from '@protos/learning_management/lms.db_pb';

interface QTypeMark {
  questionType: number;
  marks: number;
}

interface Props {
  questions: Question[];
  isMobile?: boolean;
  onSave: (marks: QTypeMark[]) => void;
}

interface QuestionTypeRow {
  label: string;
  type: Question_QuestionEnum | undefined;
  count: number;
  totalMarks: number;
  marksPerQuestion: number;
}

export default function EditMarksPanel({ questions, isMobile, onSave }: Props) {
  const typesMap = questionCountTypeWise(questions || []);
  const { qpg_assessment_task } = deserify(
    useAppSelector((state) => state.assessment)
  );
  // Convert typesMap to array of rows with calculated marks per question
  const initialRows: QuestionTypeRow[] = Object.entries(typesMap).map(
    ([label, data]: any) => ({
      label,
      type: data.type,
      count: data.count,
      totalMarks: data.marks,
      marksPerQuestion:
        data.count > 0 ? Math.round(data.marks / data.count) : 0,
    })
  );

  const [rows, setRows] = useState<QuestionTypeRow[]>(initialRows);

  useEffect(() => {
    setRows(initialRows);
  }, [questions]);

  const totalQuestions = questions?.length || 0;
  const totalMarks = rows.reduce(
    (sum, row) => sum + row.marksPerQuestion * row.count,
    0
  );

  const hasChanges = rows.some(
    (row, idx) => row.marksPerQuestion !== initialRows[idx]?.marksPerQuestion
  );

  const incrementMarks = (index: number) => {
    const newRows = [...rows];
    newRows[index].marksPerQuestion = Math.min(
      newRows[index].marksPerQuestion + 1,
      20
    );
    setRows(newRows);
  };

  const decrementMarks = (index: number) => {
    const newRows = [...rows];
    newRows[index].marksPerQuestion = Math.max(
      newRows[index].marksPerQuestion - 1,
      1
    );
    setRows(newRows);
  };

  const handleSave = async () => {
    const payload: QTypeMark[] = rows.map((row) => ({
      questionType: Number(row.type),
      marks: row.marksPerQuestion,
    }));
    console.log('Saving marks payload:', payload);
    await onSave(payload);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: pxToRem(12) }}>
      <Typography variant={isMobile ? 'h2' : 'h3'} sx={{ fontWeight: 'bold' }}>
        Edit Marks
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: pxToRem(10),
        }}
      >
        <Typography>No of Questions: {totalQuestions}</Typography>
        <Divider sx={{ bgcolor: '#E0E0E0', width: '1px', height: '100%' }} />
        <Typography variant="cardText" color={'#007CDC'} fontWeight={'600'}>
          Total Marks: {totalMarks}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: pxToRem(10) }}>
        {rows.map((row, index) => (
          <Box
            key={row.type}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: pxToRem(8),
              paddingY: pxToRem(6),
              borderBottom: '1px dashed #E6E6E6',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Typography
                variant="bodyText"
                sx={{
                  width: { xs: pxToRem(110), md: pxTovW(200), lg: pxTovW(150) },
                  display: '-webkit-box',
                  WebkitLineClamp: { xs: 1, md: 1 },
                  wordWrap: 'break-word',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textAlign: 'left',
                }}
              >
                {row.label}
              </Typography>
              <ChipBadge label={row.count} color="primary" size="small" />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: pxToRem(12),
              }}
            >
              <QuantityButton
                disableColor
                disabled={
                  qpg_assessment_task?.taskCreationStatus ===
                  TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
                }
                quantity={row.marksPerQuestion}
                incrementClickHandler={() => incrementMarks(index)}
                decrementClickHandler={() => decrementMarks(index)}
                quantityText="Mark"
                largeVariant
                minQuantity={1}
                maxQuantity={20}
              />
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: pxToRem(10) }}>
        <PrimaryButton
          disabled={
            !hasChanges ||
            qpg_assessment_task?.taskCreationStatus ===
              TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
          }
          onClick={handleSave}
          sx={{ paddingX: pxToRem(12), paddingY: pxToRem(8) }}
        >
          <Typography
            variant={isMobile ? 'h2' : 'h3'}
            sx={{ fontWeight: 'bold', color: 'white' }}
          >
            Save
          </Typography>
        </PrimaryButton>
      </Box>
    </Box>
  );
}
